/**
 * Role Management Component
 * Allows admins to create, edit, and manage roles with permissions
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../../services/api';
import { Role, Permission } from '../../types';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import PermissionChecker from './PermissionChecker';
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Key,
  Lock,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// All available permissions organized by category
const PERMISSION_CATEGORIES = {
  'Analytics': [
    'analytics:view',
    'analytics:export',
    'analytics:admin',
    'analytics:*',
  ],
  'Audit': [
    'audit:read',
    'audit:admin',
    'audit:*',
  ],
  'Contact': [
    'contact:read',
    'contact:update',
    'contact:delete',
    'contact:*',
  ],
  'Help': [
    'help:create',
    'help:read',
    'help:update',
    'help:delete',
    'help:publish',
    'help:admin',
    'help:*',
  ],
  'Order': [
    'order:read',
    'order:refund',
    'order:admin',
    'order:*',
  ],
  'Product': [
    'product:create',
    'product:read',
    'product:update',
    'product:delete',
    'product:publish',
    'product:admin',
    'product:*',
  ],
  'Settings': [
    'settings:read',
    'settings:update',
    'settings:admin',
    'settings:*',
  ],
  'User': [
    'user:create',
    'user:read',
    'user:update',
    'user:delete',
    'user:roles',
    'user:admin',
    'user:*',
  ],
} as const;

export default function RoleManagement() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as Permission[],
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getRoles();
      setRoles(data.roles);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalPermissions = roles.reduce((sum, r) => sum + r.permissions.length, 0);
    const wildcardRoles = roles.filter(r => r.permissions.some(p => p.endsWith(':*'))).length;

    return {
      total: roles.length,
      totalPermissions,
      avgPermissions: roles.length > 0 ? Math.round(totalPermissions / roles.length) : 0,
      wildcardRoles,
    };
  }, [roles]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'total',
      label: 'Total Roles',
      value: stats.total,
      icon: Shield,
      variant: 'primary',
    },
    {
      id: 'permissions',
      label: 'Total Permissions',
      value: stats.totalPermissions,
      icon: Key,
      variant: 'default',
    },
    {
      id: 'avg',
      label: 'Avg. per Role',
      value: stats.avgPermissions,
      icon: Lock,
      variant: 'success',
    },
  ], [stats]);

  const handleCreateRole = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: [] });
    setShowForm(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permissions: role.permissions,
    });
    setShowForm(true);
  };

  const handleTogglePermission = (permission: Permission) => {
    const isSelected = formData.permissions.includes(permission);

    if (isSelected) {
      setFormData({
        ...formData,
        permissions: formData.permissions.filter(p => p !== permission),
      });
    } else {
      setFormData({
        ...formData,
        permissions: [...formData.permissions, permission],
      });
    }
  };

  const handleSelectAllInCategory = (_category: string, permissions: readonly string[]) => {
    const allSelected = permissions.every(p => formData.permissions.includes(p as Permission));

    if (allSelected) {
      // Remove all from this category
      setFormData({
        ...formData,
        permissions: formData.permissions.filter(p => !permissions.includes(p)),
      });
    } else {
      // Add all from this category
      const newPermissions = new Set([...formData.permissions, ...permissions]);
      setFormData({
        ...formData,
        permissions: Array.from(newPermissions) as Permission[],
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingRole) {
        await adminApi.updateRole(editingRole.id, formData);
      } else {
        await adminApi.createRole(formData);
      }

      setShowForm(false);
      loadRoles();
    } catch (err: any) {
      setError(err.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm('Are you sure you want to delete this role? Users with this role will lose these permissions.')) {
      return;
    }

    try {
      await adminApi.deleteRole(roleId);
      loadRoles();
    } catch (err: any) {
      setError(err.message || 'Failed to delete role');
    }
  };

  const toggleRoleExpanded = (roleId: string) => {
    const newExpanded = new Set(expandedRoles);
    if (newExpanded.has(roleId)) {
      newExpanded.delete(roleId);
    } else {
      newExpanded.add(roleId);
    }
    setExpandedRoles(newExpanded);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Role Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage roles and permissions for your platform
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadRoles}
            disabled={loading}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleCreateRole}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus size={16} />
            Create Role
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <section>
        <StatsWidget stats={statItems} columns={3} loading={loading} />
      </section>

      {/* Error Display */}
      {error && (
        <div className="p-4 rounded-xl flex items-start gap-3 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800">
          <AlertTriangle size={20} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Permission Checker */}
      <PermissionChecker />

      {/* Role Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <Shield size={20} className="text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {editingRole ? 'Edit Role' : 'Create New Role'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {editingRole ? 'Modify role permissions' : 'Define a new role with specific permissions'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Role Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., product-manager"
                    required
                    disabled={!!editingRole}
                  />
                  {editingRole && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Role name cannot be changed after creation
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Describe what this role is for..."
                    required
                  />
                </div>

                {/* Permissions */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Permissions ({formData.permissions.length} selected)
                    </label>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                    {Object.entries(PERMISSION_CATEGORIES).map(([category, permissions]) => {
                      const selectedInCategory = permissions.filter(p =>
                        formData.permissions.includes(p as Permission)
                      ).length;
                      const allSelected = selectedInCategory === permissions.length;

                      return (
                        <div
                          key={category}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                        >
                          <div
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 cursor-pointer"
                            onClick={() => handleSelectAllInCategory(category, permissions)}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() => {}}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                {category}
                              </h4>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({selectedInCategory}/{permissions.length})
                              </span>
                            </div>
                          </div>
                          <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                            {permissions.map((permission) => (
                              <label
                                key={permission}
                                className={`flex items-center space-x-2 cursor-pointer p-2 rounded-lg transition-colors ${
                                  formData.permissions.includes(permission as Permission)
                                    ? 'bg-primary-50 dark:bg-primary-900/20'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.includes(permission as Permission)}
                                  onChange={() => handleTogglePermission(permission as Permission)}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className={`text-xs font-mono ${
                                  permission.endsWith(':*')
                                    ? 'text-amber-600 dark:text-amber-400 font-bold'
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  {permission}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Wildcard (*) grants all permissions for that resource
                  </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary inline-flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        {editingRole ? 'Update Role' : 'Create Role'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Role List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48" />
                </div>
                <div className="flex gap-2">
                  <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                  <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
                </div>
              </div>
              <div className="flex gap-2">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : roles.length === 0 ? (
        <div className="card p-12 text-center">
          <Shield size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Roles Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Create your first role to get started with permission management.
          </p>
          <button onClick={handleCreateRole} className="btn btn-primary inline-flex items-center gap-2">
            <Plus size={16} />
            Create Role
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {roles.map((role) => {
            const isExpanded = expandedRoles.has(role.id);
            const isSystemRole = ['customer', 'administrator'].includes(role.id);

            return (
              <div
                key={role.id}
                className="card overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        isSystemRole
                          ? 'bg-amber-100 dark:bg-amber-900/30'
                          : 'bg-primary-100 dark:bg-primary-900/30'
                      }`}>
                        <Shield size={20} className={
                          isSystemRole
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-primary-600 dark:text-primary-400'
                        } />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {role.name}
                          </h3>
                          {isSystemRole && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                              System
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {role.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditRole(role)}
                        className="btn btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1"
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                      {!isSystemRole && (
                        <button
                          onClick={() => handleDeleteRole(role.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Permission Summary */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => toggleRoleExpanded(role.id)}
                      className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    >
                      <Key size={14} />
                      <span className="font-medium">{role.permissions.length} permissions</span>
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {isExpanded && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {role.permissions.map((permission) => (
                          <span
                            key={permission}
                            className={`px-2 py-1 text-xs font-mono rounded ${
                              permission.endsWith(':*')
                                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {permission}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help with roles and permissions?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn about role-based access control and best practices
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}
